import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ImageHotspotPlayer = ({ gameData, onComplete }) => {
  const [answeredHotspots, setAnsweredHotspots] = useState({});
  const [activeHotspot, setActiveHotspot] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [gameFinished, setGameFinished] = useState(false);
  const imageRef = useRef(null);

  const hotspots = gameData?.hotspots || [];
  const imageUrl = gameData?.hotspotImage || '';

  const handleHotspotClick = (hotspot, e) => {
    e?.stopPropagation();
    if (gameFinished) return;
    setActiveHotspot(hotspot);
    setSelectedOption(null);
    setSubmitted(false);
  };

  const handleSubmitAnswer = () => {
    if (selectedOption === null || selectedOption === undefined) return;
    const isCorrect = String(selectedOption) === String(activeHotspot?.correctAnswer);
    setSubmitted(true);
    setAnsweredHotspots(prev => ({
      ...prev,
      [activeHotspot?.id]: { answered: true, correct: isCorrect, selectedOption }
    }));
  };

  const handleClosePopup = () => {
    setActiveHotspot(null);
    setSelectedOption(null);
    setSubmitted(false);
  };

  const handleFinish = () => {
    const total = hotspots?.length || 1;
    const correct = Object.values(answeredHotspots)?.filter(a => a?.correct)?.length;
    setGameFinished(true);
    setActiveHotspot(null);
    onComplete?.({ correct, total, score: correct / total, gameType: 'image-hotspot' });
  };

  const allAnswered = hotspots?.length > 0 && hotspots?.every(h => answeredHotspots?.[h?.id]?.answered);

  const getHotspotStatus = (hotspot) => {
    const answer = answeredHotspots?.[hotspot?.id];
    if (!answer) return 'unanswered';
    return answer?.correct ? 'correct' : 'incorrect';
  };

  if (!imageUrl || hotspots?.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Icon name="ImageOff" size={40} className="mx-auto mb-3" />
        <p className="text-sm">No hotspot image configured for this game.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Progress */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          {Object.keys(answeredHotspots)?.length} / {hotspots?.length} hotspots answered
        </span>
        <div className="flex items-center space-x-1">
          {hotspots?.map((h, i) => {
            const status = getHotspotStatus(h);
            return (
              <div
                key={h?.id}
                className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold border ${
                  status === 'correct' ?'bg-green-100 border-green-400 text-green-700'
                    : status === 'incorrect' ?'bg-red-100 border-red-400 text-red-700' :'bg-muted border-border text-muted-foreground'
                }`}
              >
                {i + 1}
              </div>
            );
          })}
        </div>
      </div>

      {/* Image with hotspot markers */}
      <div className="relative rounded-xl overflow-hidden border-2 border-border bg-muted/20">
        <img
          ref={imageRef}
          src={imageUrl}
          alt="Interactive hotspot image - click the numbered markers to answer questions"
          className="w-full object-contain max-h-96"
          draggable={false}
        />
        {hotspots?.map((hotspot, index) => {
          const status = getHotspotStatus(hotspot);
          const isActive = activeHotspot?.id === hotspot?.id;
          return (
            <button
              key={hotspot?.id}
              className={`absolute transition-all duration-200 ${
                isActive ? 'z-20' : 'z-10'
              }`}
              style={{
                left: `${hotspot?.x}%`,
                top: `${hotspot?.y}%`,
                transform: 'translate(-50%, -50%)'
              }}
              onClick={(e) => handleHotspotClick(hotspot, e)}
              title={`Hotspot ${index + 1}`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shadow-lg border-2 transition-all ${
                  isActive
                    ? 'bg-yellow-400 border-yellow-600 text-yellow-900 scale-125 ring-2 ring-yellow-300'
                    : status === 'correct' ?'bg-green-500 border-white text-white'
                    : status === 'incorrect' ?'bg-red-500 border-white text-white' :'bg-primary border-white text-white hover:scale-110 animate-pulse'
                }`}
              >
                {status === 'correct' ? (
                  <Icon name="Check" size={14} />
                ) : status === 'incorrect' ? (
                  <Icon name="X" size={14} />
                ) : (
                  index + 1
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Instruction */}
      {!allAnswered && !gameFinished && (
        <p className="text-xs text-muted-foreground text-center">
          <Icon name="MousePointerClick" size={12} className="inline mr-1" />
          Click on the numbered markers to answer questions
        </p>
      )}

      {/* Finish button */}
      {allAnswered && !gameFinished && (
        <Button onClick={handleFinish} iconName="Check" fullWidth>
          Submit All Answers
        </Button>
      )}

      {/* Question popup modal */}
      {activeHotspot && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={handleClosePopup}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-5 animate-in fade-in zoom-in duration-200"
            onClick={(e) => e?.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center text-sm font-bold text-white">
                  {hotspots?.findIndex(h => h?.id === activeHotspot?.id) + 1}
                </div>
                <span className="text-sm font-semibold text-foreground">Hotspot Question</span>
              </div>
              <button
                onClick={handleClosePopup}
                className="text-muted-foreground hover:text-foreground transition-organic"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            {/* Question */}
            <p className="text-base font-medium text-foreground mb-4">
              {activeHotspot?.question || 'What do you see here?'}
            </p>

            {/* Options */}
            <div className="space-y-2 mb-4">
              {(activeHotspot?.options || [])?.map((option, i) => {
                if (!option?.trim()) return null;
                const isSelected = selectedOption === i;
                const isCorrect = String(i) === String(activeHotspot?.correctAnswer);
                let optionClass = 'border-border bg-card hover:border-primary hover:bg-primary/5';
                if (submitted) {
                  if (isCorrect) {
                    optionClass = 'border-green-500 bg-green-50 text-green-800';
                  } else if (isSelected && !isCorrect) {
                    optionClass = 'border-red-400 bg-red-50 text-red-800';
                  } else {
                    optionClass = 'border-border bg-muted/30 text-muted-foreground';
                  }
                } else if (isSelected) {
                  optionClass = 'border-primary bg-primary/10 text-primary';
                }
                return (
                  <button
                    key={i}
                    disabled={submitted}
                    onClick={() => !submitted && setSelectedOption(i)}
                    className={`w-full flex items-center space-x-3 p-3 rounded-lg border-2 text-left transition-all ${
                      optionClass
                    } ${submitted ? 'cursor-default' : 'cursor-pointer'}`}
                  >
                    <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      submitted && isCorrect
                        ? 'border-green-500 bg-green-500 text-white'
                        : submitted && isSelected && !isCorrect
                        ? 'border-red-400 bg-red-400 text-white'
                        : isSelected
                        ? 'border-primary bg-primary text-white' :'border-current'
                    }`}>
                      {submitted && isCorrect ? (
                        <Icon name="Check" size={10} />
                      ) : submitted && isSelected && !isCorrect ? (
                        <Icon name="X" size={10} />
                      ) : (
                        String.fromCharCode(65 + i)
                      )}
                    </span>
                    <span className="text-sm">{option}</span>
                  </button>
                );
              })}
            </div>

            {/* Feedback */}
            {submitted && (
              <div className={`flex items-center space-x-2 p-3 rounded-lg mb-3 ${
                String(selectedOption) === String(activeHotspot?.correctAnswer)
                  ? 'bg-green-50 border border-green-200' :'bg-red-50 border border-red-200'
              }`}>
                <Icon
                  name={String(selectedOption) === String(activeHotspot?.correctAnswer) ? 'CheckCircle2' : 'XCircle'}
                  size={18}
                  className={String(selectedOption) === String(activeHotspot?.correctAnswer) ? 'text-green-600' : 'text-red-500'}
                />
                <span className={`text-sm font-medium ${
                  String(selectedOption) === String(activeHotspot?.correctAnswer) ? 'text-green-700' : 'text-red-700'
                }`}>
                  {String(selectedOption) === String(activeHotspot?.correctAnswer)
                    ? 'Correct! Well done!'
                    : `Incorrect. The correct answer is ${String.fromCharCode(65 + parseInt(activeHotspot?.correctAnswer))}.`
                  }
                </span>
              </div>
            )}

            {/* Action button */}
            {!submitted ? (
              <Button
                onClick={handleSubmitAnswer}
                disabled={selectedOption === null || selectedOption === undefined}
                iconName="Check"
                fullWidth
              >
                Submit Answer
              </Button>
            ) : (
              <Button onClick={handleClosePopup} iconName="ArrowRight" iconPosition="right" fullWidth>
                {allAnswered ? 'Close' : 'Next Hotspot'}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageHotspotPlayer;
